import React from 'react';
import Link from 'next/link';
import { ExternalLink } from 'lucide-react';

interface SecondaryAction {
  label: string;
  href?: string;
  icon: any;
  isExternal?: boolean;
  onClick?: () => void;
}

interface OnboardingCardProps {
  title: string;
  description: string;
  icon: any;
  buttonText?: string;
  buttonHref?: string;
  onClick?: () => void;
  secondaryActions?: SecondaryAction[];
}

const OnboardingCard = ({
  title,
  description,
  icon: Icon,
  buttonText,
  buttonHref,
  onClick,
  secondaryActions = [],
}: OnboardingCardProps) => (
  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col h-full hover:shadow-md transition-all duration-300">
    <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-teal-600" />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm mb-6 flex-grow">{description}</p>

    <div className="flex flex-col gap-3 mt-auto">
      {buttonText && (
        <>
          {buttonHref ? (
            <Link href={buttonHref} passHref legacyBehavior>
              <a className="w-full bg-[#2CA6A4] text-white py-2.5 rounded-xl font-semibold text-center hover:bg-[#238b8a] transition-colors flex items-center justify-center gap-2">
                {buttonText}
                <ExternalLink className="w-4 h-4" />
              </a>
            </Link>
          ) : (
            <button
              onClick={onClick}
              className="w-full bg-[#2CA6A4] text-white py-2.5 rounded-xl font-semibold text-center hover:bg-[#238b8a] transition-colors flex items-center justify-center gap-2"
            >
              {buttonText}
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </>
      )}

      {secondaryActions.map((action, idx) => {
        const content = (
          <>
            <action.icon className="w-4 h-4" />
            {action.label}
          </>
        );
        const className =
          'w-full bg-gray-50/50 text-gray-700 py-2 rounded-xl text-sm font-medium text-center hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 border border-gray-200 cursor-pointer';

        if (action.onClick) {
          return (
            <button key={idx} onClick={action.onClick} className={className}>
              {content}
            </button>
          );
        }

        return (
          <a
            key={idx}
            href={action.href}
            target={action.isExternal ? '_blank' : '_self'}
            rel={action.isExternal ? 'noopener noreferrer' : ''}
            className={className}
          >
            {content}
          </a>
        );
      })}
    </div>
  </div>
);

export default OnboardingCard;
